import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp, FaUser } from 'react-icons/fa';
import styled from 'styled-components';

/* Estilos idênticos ao dropdown de responsáveis em SupportTicketDetails */
const ResponsibleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const ResponsibleLabel = styled.span`
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
  font-size: 0.95rem;

  svg {
    color: #404040;
  }
`;

const ResponsibleFilterContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ResponsibleDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: 12px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 180px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-primary);

  &:hover:not(:disabled) {
    border-color: var(--color-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    box-shadow: none;
  }
`;

const ResponsibleDropdownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
`;

const ResponsibleDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.35rem;
  min-width: 220px;
  max-height: 320px;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 20;
`;

const ResponsibleSearchInput = styled.input`
  margin: 0.5rem 0.5rem 0;
  padding: 0.45rem 0.65rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.82rem;
  outline: none;

  &:focus {
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.25);
  }
`;

const ResponsibleOptionsList = styled.div`
  padding: 0.5rem;
  overflow-y: auto;
  max-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex-shrink: 0;
`;

const ResponsibleOptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-text-primary);
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-bg-secondary);
  }

  input {
    accent-color: var(--color-text-secondary);
  }
`;

const ResponsibleMenuActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0.5rem;
  border-top: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
`;

const ResponsibleClearButton = styled.button`
  background: none;
  border: none;
  color: #d63031;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #adb5bd;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const ResponsibleApplyButton = styled.button`
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.35rem 0.65rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReloadUsersButton = styled.button`
  margin-top: 6px;
  padding: 6px 12px;
  font-size: 0.8rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
`;

export const normalizeResponsibleId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id);
};

const buildUserOptions = (users, extraUsers = []) => {
  const byId = new Map();
  const addUser = (user) => {
    if (!user) return;
    const id = normalizeResponsibleId(user.id ?? user);
    if (!id) return;
    if (!byId.has(id)) {
      byId.set(id, {
        id,
        name: user.name || user.email || 'Usuário',
        email: user.email || '',
      });
    }
  };

  (users || []).forEach(addUser);
  (extraUsers || []).forEach(addUser);

  return [...byId.values()].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
  );
};

const ResponsibleMultiSelect = ({
  users = [],
  extraUsers = [],
  selectedIds = [],
  onSelectedIdsChange,
  disabled = false,
  label = 'Responsáveis:',
  showUserIcon = true,
  fieldId = 'responsible-multi-select',
  onReloadUsers,
  triggerContent,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftIds, setDraftIds] = useState([]);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const userOptions = useMemo(
    () => buildUserOptions(users, extraUsers),
    [users, extraUsers]
  );

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return userOptions;
    return userOptions.filter((user) => {
      const name = String(user.name || '').toLowerCase();
      const email = String(user.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [userOptions, search]);

  const currentIds = useMemo(
    () =>
      (Array.isArray(selectedIds) ? selectedIds : [])
        .map(normalizeResponsibleId)
        .filter(Boolean),
    [selectedIds]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const summaryLabel = currentIds.length
    ? `${currentIds.length} responsável(eis) selecionado(s)`
    : 'Selecione os responsáveis';

  return (
    <ResponsibleContainer className="responsible-container">
      <ResponsibleLabel className="label">
        {showUserIcon && <FaUser />}
        {label}
      </ResponsibleLabel>
      <ResponsibleFilterContainer ref={containerRef}>
        <ResponsibleDropdownButton
          id={fieldId}
          type="button"
          onClick={() => {
            if (disabled) return;
            if (!isOpen) {
              setDraftIds([...currentIds]);
              setSearch('');
            }
            setIsOpen((prev) => !prev);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {triggerContent ?? <span>{summaryLabel}</span>}
          <ResponsibleDropdownIcon>
            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
          </ResponsibleDropdownIcon>
        </ResponsibleDropdownButton>
        {isOpen && (
          <ResponsibleDropdownMenu>
            <ResponsibleSearchInput
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou e-mail..."
              autoFocus
            />
            <ResponsibleOptionsList>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <ResponsibleOptionRow key={user.id}>
                    <input
                      type="checkbox"
                      value={normalizeResponsibleId(user.id)}
                      checked={draftIds.includes(normalizeResponsibleId(user.id))}
                      onChange={() => {
                        const id = normalizeResponsibleId(user.id);
                        setDraftIds((prev) =>
                          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                        );
                      }}
                    />
                    <span>{user.name || user.email}</span>
                  </ResponsibleOptionRow>
                ))
              ) : (
                <span style={{ fontSize: '0.7rem', color: '#6c757d', fontStyle: 'italic' }}>
                  {userOptions.length === 0
                    ? 'Nenhum usuário habilitado para atribuição'
                    : 'Nenhum resultado para a busca'}
                </span>
              )}
            </ResponsibleOptionsList>
            <ResponsibleMenuActions>
              <ResponsibleClearButton
                type="button"
                onClick={() => setDraftIds([])}
                disabled={draftIds.length === 0}
              >
                Limpar tudo
              </ResponsibleClearButton>
              <ResponsibleApplyButton
                type="button"
                onClick={() => {
                  onSelectedIdsChange([...draftIds]);
                  setIsOpen(false);
                }}
              >
                Aplicar
              </ResponsibleApplyButton>
            </ResponsibleMenuActions>
          </ResponsibleDropdownMenu>
        )}
      </ResponsibleFilterContainer>
      {onReloadUsers && userOptions.length === 0 && (
        <ReloadUsersButton type="button" onClick={onReloadUsers}>
          Recarregar Usuários
        </ReloadUsersButton>
      )}
    </ResponsibleContainer>
  );
};

ResponsibleMultiSelect.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  extraUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onSelectedIdsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  showUserIcon: PropTypes.bool,
  fieldId: PropTypes.string,
  onReloadUsers: PropTypes.func,
  triggerContent: PropTypes.node,
};

export default ResponsibleMultiSelect;
